import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { MapPin } from 'lucide-react'
import { locationsApi } from '../api'
import type { Location } from '@/types/entities'
import { buildAppImageUrl } from '@/lib/imagePaths'

interface LocationViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    location: Location | null
}

export function LocationViewDialog({ open, onOpenChange, location: locationProp }: LocationViewDialogProps) {
    const { t } = useTranslation()

    // Fetch full location details (including lat/lng/image) when dialog opens
    const { data: fullLocationData } = useQuery({
        queryKey: ['location-detail', locationProp?.id],
        queryFn: async () => {
            if (!locationProp?.id) return null
            const res = await locationsApi.get(locationProp.id)
            return (res.data as any)?.data || res.data
        },
        enabled: open && !!locationProp?.id,
    })

    const location = fullLocationData ? { ...locationProp, ...fullLocationData } as Location : locationProp
    if (!location) return null

    const hasCoordinates = location.latitude != null && location.longitude != null
    const hasImage = !!location.imageFilename
    const hasRightColumn = hasCoordinates || hasImage

    // Build Google Maps embed URL
    const mapEmbedUrl = hasCoordinates
        ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}&z=15&output=embed`
        : null

    // Build address string for the map link
    const addressParts = [location.description, location.address1, location.city, location.postcode, location.country].filter(Boolean)
    const mapsSearchUrl = hasCoordinates
        ? `https://www.google.com/maps?q=${location.latitude},${location.longitude}`
        : `https://www.google.com/maps/search/${encodeURIComponent(addressParts.join(', '))}`

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={hasRightColumn ? 'sm:max-w-[800px]' : 'sm:max-w-[500px]'}>
                <DialogHeader>
                    <DialogTitle>{t('locations.viewLocation')}</DialogTitle>
                    <DialogDescription>
                        {t('locations.viewLocationDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className={hasRightColumn ? 'grid grid-cols-2 gap-6' : ''}>
                    {/* Left — Details */}
                    <div className="p-1">
                        <div className="text-sm space-y-3">
                            <div>
                                <span className="text-muted-foreground text-xs">{t('locations.description')}</span>
                                <p className="font-medium">{location.description || '—'}</p>
                            </div>
                            {location.address1 && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.address1')}</span>
                                    <p>{location.address1}</p>
                                </div>
                            )}
                            {location.address2 && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.address2')}</span>
                                    <p>{location.address2}</p>
                                </div>
                            )}
                            {location.county && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.county')}</span>
                                    <p>{location.county}</p>
                                </div>
                            )}
                            {location.postcode && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.postcode')}</span>
                                    <p>{location.postcode}</p>
                                </div>
                            )}
                            {location.category && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.category')}</span>
                                    <p>{location.category}</p>
                                </div>
                            )}
                            {(location.city || location.country) && (
                                <div>
                                    <span className="text-muted-foreground text-xs">{t('locations.worldLocation')}</span>
                                    <p>{[location.city, location.country].filter(Boolean).join(', ')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right — Map + Image */}
                    {hasRightColumn && (
                        <div className="space-y-4">
                            {/* Google Maps */}
                            {hasCoordinates && mapEmbedUrl && (
                                <div className="space-y-1">
                                    <a
                                        href={mapsSearchUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                                    >
                                        <MapPin className="h-3 w-3" />
                                        Open in Google Maps
                                    </a>
                                    <div className="rounded-lg overflow-hidden border" style={{ height: hasImage ? '180px' : '280px' }}>
                                        <iframe
                                            title="Location map"
                                            src={mapEmbedUrl}
                                            width="100%"
                                            height="100%"
                                            style={{ border: 0 }}
                                            allowFullScreen
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Location image */}
                            {hasImage && (
                                <div className="rounded-lg overflow-hidden border">
                                    <img
                                        src={buildAppImageUrl('locations', location.imageFilename ?? '')}
                                        alt={`${location.description} floor plan`}
                                        className="w-full object-contain bg-muted/30"
                                        style={{ maxHeight: hasCoordinates ? '180px' : '300px' }}
                                    />
                                </div>
                            )}

                            {/* Fallback — no coordinates but has city, show static map link */}
                            {!hasCoordinates && (location.city || location.country) && (
                                <a
                                    href={mapsSearchUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                >
                                    <MapPin className="h-4 w-4" />
                                    View {[location.city, location.country].filter(Boolean).join(', ')} on Google Maps
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
